import { BaseListViewStore, BooleanCellData } from 'core/stores';
import { CarrierStore } from 'modules/carrier/stores';
import { LookupStore } from 'modules/lookup/stores';
import { localStorageHandler } from 'core/services';
import { notificationError, TableFilterUtility, checkForColumnWidth, isSuperAdminOrganization } from 'core/utils';
import { observable, action, reaction, makeObservable } from 'mobx';
import { ModalParams } from 'core/models';
import _ from 'lodash';
import { CarrierEdit } from '../pages';

class CarrierListViewStore extends BaseListViewStore {
	@observable change = false;
	isSuperAdmin = false;
	localStorageWidthKey = 'carriers-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new CarrierStore(rootStore),
			queryParams: {
				embed: ['carrierType', 'seller'],
			},
			tableData: {
				columns: [
					{
						key: 'name',
						title: 'CARRIERVIEW.NAME',
						selected: true,
						width: 200,
						filter: null,
						sortable: true,
						selected: true,
					},
					{
						key: 'seller.applicationTitle',
						title: 'CARRIERVIEW.SELLER',
						selected: false,
						width: 200,
						filter: null,
						sortable: true,
						selected: true,
						enabled: isSuperAdminOrganization(rootStore),
					},
					{
						key: 'carrierType.name',
						title: 'CARRIERVIEW.CARRIERTYPE',
						selected: true,
						width: 200,
						filter: null,
						sortable: true,
						selected: true,
					},
					{
						key: 'isThirdParty',
						title: 'CARRIERVIEW.ISTHIRDPARTY',
						dataCell: BooleanCellData,
						selected: true,
						width: 200,
						filter: null,
						sortable: true,
						selected: true,
					},
				],
				sortObject: [
					{
						field: 'name',
						dir: 'asc',
					},
				],
				actions: {
					onEdit: async item => this.routes.edit(item.id),
					onDelete: async item => this.openConfirmationModal(this.getDeleteConfirmModalProps(item)),
				},
			},
			permissions: {
				onEdit: () => this.authStore.hasPermission('shippingCarrierSection.update'),
				onDelete: () => this.authStore.hasPermission('shippingCarrierSection.remove'),
			},
			routes: {
				edit: id => this.rootStore.routerStore.setQueryParams({ editId: id }),
				create: () => this.rootStore.routerStore.setQueryParams({ create: null }),
				list: () => this.rootStore.routerStore.goTo('master.app.main.carrier.list', {}),
			},
		});
		makeObservable(this);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		if (isSuperAdminOrganization(this.rootStore)) {
			this.isSuperAdmin = true;
			await this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		await this.queryUtility.initialize(this.findCarrier);
		reaction(
			() => this.rootStore.routerStore.routerState,
			state => {
				if (state.routeName !== 'master.app.main.carrier.list') {
					return;
				}

				const { editId, create } = state.queryParams;
				if (editId !== undefined || create !== undefined) {
					this.openModal({
						onSuccess: async () => {
							await this.queryUtility.initialize(false);
							this.closeEditModal();
						},
						onClose: this.closeEditModal,
						component: CarrierEdit,
					});
				} else {
					this.rootStore.viewStore.selectedModal.setOpen(false);
				}
			}
		);
		this.tableFilter = new TableFilterUtility({
			lookupStore: new LookupStore(this.rootStore),
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
		});
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.setButtons();
		this.setSearchVisible();
	};

	@action.bound setAsChanged() {
		this.change = !this.change;
	}

	@action.bound setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-plus spc--right--sml',
					onClick: this.routes.create,
					authorization: 'shippingCarrierSection.create',
					text: 'CARRIERVIEW.NEW',
				},
			],
		});
	}

	@action.bound async findCarrier() {
		this.suspend();
		this.queryUtility.setFilterParams(this);
		await this.listStore.find(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.setHeaderModes();
		this.resume();
	}

	@action.bound async deleteCarrier(item) {
		this.suspend();
		try {
			await this.listStore.deleteCarrier(item);
			this.rootStore.notificationStore.success(['LOOKUPDELETE.CARRIER', { name: item.name }]);
			await this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	@action.bound closeEditModal() {
		this.rootStore.routerStore.updateQueryParams({
			editId: undefined,
			create: undefined,
			...this.queryUtility.getFilterUrlParams(),
		});
	}

	getDeleteConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.deleteCarrier(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['LOOKUPDELETECONFIRM.CARRIER', { name: item.name }],
			data: item,
		};
	};
}

export default CarrierListViewStore;



// WEBPACK FOOTER //
// ./src/modules/carrier/stores/CarrierListViewStore.js